// Единые правила доступности времени для записи.
// Раньше эти константы и функции жили только в BookingCalendar.js, из-за чего
// админ-календарь не показывал, какие часы закрыты для учеников. Теперь модуль
// общий: и календарь ученика, и админка считают доступность одинаково.
import moment from 'moment-timezone';

export const TZ_BOGOTA = 'America/Bogota';
export const TZ_ROME = 'Europe/Rome';

// Коллекция Firestore с «открытыми сверх расписания» часами (см. админку).
export const OPEN_HOURS_COLLECTION = 'openHours';

export const BLOCKED_ISO_DATES = new Set([
  "2025-04-25",
  "2025-04-26",
  "2025-10-16",
  "2025-10-17",
  "2025-10-18",
  "2025-10-19",
  "2026-09-03",
]);


export function isRomeTZ() { return true; }
export function getTZForDate() { return TZ_ROME; }
export function isAvailableHour(date) {
  const d = moment.tz(date, TZ_ROME);
  return d.hour() >= 8 && d.hour() < 21;
}
export function isWeekendBlocked() { return false; }

// Дата в часовом поясе преподавателя: админ вводит «11 сентября, 18:00–20:00»
// в своём времени, а ученик в другом поясе должен увидеть тот же реальный час.
export function teacherDayKey(date) {
  return moment.tz(date, getTZForDate(date)).format('YYYY-MM-DD');
}

function toMinutes(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (Number.isNaN(h)) return null;
  return h * 60 + (m || 0);
}

// openHours: [{ date: 'YYYY-MM-DD', startTime: 'HH:mm', endTime: 'HH:mm' }, ...]
export function isExtraOpen(date, openHours) {
  if (!openHours || openHours.length === 0) return false;
  const inTeacherTZ = moment.tz(date, getTZForDate(date));
  const key = inTeacherTZ.format('YYYY-MM-DD');
  const minutes = inTeacherTZ.hour() * 60 + inTeacherTZ.minute();
  return openHours.some((o) => {
    if (o.date !== key) return false;
    const from = toMinutes(o.startTime);
    const to = toMinutes(o.endTime);
    if (from === null || to === null) return false;
    return minutes >= from && minutes < to;
  });
}

// Есть ли у дня хоть один открытый сверх расписания интервал (для месячного вида).
export function hasExtraOpenDay(date, openHours) {
  if (!openHours || openHours.length === 0) return false;
  const key = teacherDayKey(date);
  return openHours.some((o) => o.date === key);
}

// Главная проверка: закрыто ли это время для записи учеников.
// Индивидуально открытые часы сильнее всех запретов расписания — так можно
// открыть вечер даже в выходной или в день из BLOCKED_ISO_DATES.
export function isSlotUnavailable(date, openHours) {
  if (isExtraOpen(date, openHours)) return false;
  const iso = teacherDayKey(date);
  return BLOCKED_ISO_DATES.has(iso) || isWeekendBlocked(date) || !isAvailableHour(date);
}

// Закрыт ли день целиком (для затемнения ячейки в месячном виде).
export function isDayUnavailable(date, openHours) {
  if (hasExtraOpenDay(date, openHours)) return false;
  const iso = teacherDayKey(date);
  return BLOCKED_ISO_DATES.has(iso) || isWeekendBlocked(date);
}

// Абсолютные границы открытого интервала (для сравнения с выделением в календаре).
export function openHoursInterval(o) {
  return {
    start: moment.tz(`${o.date} ${o.startTime}`, 'YYYY-MM-DD HH:mm', TZ_ROME).toDate(),
    end: moment.tz(`${o.date} ${o.endTime}`, 'YYYY-MM-DD HH:mm', TZ_ROME).toDate(),
  };
}

// Выделение мышью в календаре → запись для коллекции openHours.
// Время сохраняем в поясе преподавателя: тогда ученик в любой точке мира
// увидит открытым тот же реальный час.
export function selectionToOpenHours(start, end) {
  const tz = getTZForDate(start);
  const from = moment.tz(start, tz);
  const to = moment.tz(end, tz);
  const date = from.format('YYYY-MM-DD');
  const endTime = to.format('YYYY-MM-DD') === date ? to.format('HH:mm') : '24:00';
  return { date, startTime: from.format('HH:mm'), endTime };
}

// Нормализация документа коллекции openHours.
export function normalizeOpenHours(docs) {
  return docs
    .filter((o) => o && o.date && o.startTime && o.endTime)
    .sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime));
}
